import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  Search,
  RefreshCw,
  Package,
  AlertTriangle,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { SkeletonProductCard, SkeletonList } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { CompartmentBuilder, Compartment } from '@/components/ui/input'
import { LocationSelect } from '@/components/ui/location-select'
import {
  CapacityErrorBoundary,
  useErrorBoundary,
} from '@/components/ui/error-message'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { Id } from '../../../../convex/_generated/dataModel'
import {
  useProducts,
  useGenerateCapacityArray,
  useLocationsByProject,
  useVehiclesByProject,
  useSkills,
  useCalculateJobCapacityArrays,
  useValidateJobAgainstVehicle,
  useTimeFormat,
} from '@/hooks/useVRPData'

// Filter products to only include fields expected by backend validator
function filterProductsForBackend(products: any[]) {
  return products.map(product => ({
    _id: product._id,
    category: product.category,
    compartmentType: product.compartmentType,
    name: product.name,
    unitType: product.unitType,
    volumePerUnitM3: product.volumePerUnitM3,
    weightPerUnitKg: product.weightPerUnitKg,
  }))
}

// Convert frontend compartment structure to backend schema
function convertCompartmentsToBackendSchema(
  frontendCompartments: Compartment[]
) {
  return frontendCompartments.map((comp, index) => {
    // Map product IDs to product type string
    // For now, use the compartment type as the product type
    // This should be enhanced to map product IDs to their actual types
    const productType = comp.type || 'general'

    // Determine capacity value and unit based on compartment type and capacity array
    let capacityValue = 0
    let capacityUnit = 'liters'
    // CRITICAL FIX: Use 3-slot allocation [weight, volume, count] to match backend algorithm
    const arrayIndices = [index * 3, index * 3 + 1, index * 3 + 2]

    // Map based on compartment type (safely handle undefined type)
    const compartmentType = comp.type || 'general'

    if (compartmentType.includes('fuel')) {
      // For fuel compartments, use volume (index 1 in capacity array)
      capacityValue = comp.capacity?.[1] || 0
      capacityUnit = 'liters'
    } else if (
      compartmentType.includes('parcel') ||
      compartmentType.includes('package')
    ) {
      // For parcel compartments, use count (index 2)
      capacityValue = comp.capacity?.[2] || 0
      capacityUnit = 'items'
    } else {
      // For ambient/general, use weight (index 0)
      capacityValue = comp.capacity?.[0] || 0
      capacityUnit = 'kg'
    }

    return {
      id: comp.id,
      name: comp.name,
      productType: productType, // Backend expects single string
      capacityValue: capacityValue,
      capacityUnit: capacityUnit,
      arrayIndices: arrayIndices,
    }
  })
}

// Field configuration types
export interface FieldConfig {
  key: string
  label: string
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'select'
    | 'searchable_select'
    | 'multi-select'
    | 'boolean'
    | 'date'
    | 'email'
    | 'tel'
    | 'compartments'
    | 'location-select'
    | 'delivery-items'
    | 'pickup-items'
    | 'vehicle-select'
    | 'duration'
    | 'skills-select'
  placeholder?: string
  options?: { value: string; label: string; keywords?: string[] }[]
  required?: boolean
  group?: string
  helpText?: string
  min?: number
  max?: number
  // SearchableSelect specific options
  searchPlaceholder?: string
  emptyMessage?: string
  maxHeight?: number
}

export interface EditModalData {
  id: Id<'projects'> | Id<'scenarios'> | Id<'datasets'> | Id<'vehicles'>
  name: string
  description?: string
  type: 'project' | 'scenario' | 'dataset' | 'vehicle'
  // Extended project fields
  currency?: string
  projectType?: string
  industry?: string
  geographicRegion?: string
  priority?: string
  estimatedVehicles?: number
  estimatedJobs?: number
  planningHorizonDays?: number
  timezone?: string
  contactPerson?: string
  contactEmail?: string
  contactPhone?: string
  tags?: string[]
  notes?: string
  // Extended scenario fields
  startDate?: number
  endDate?: number
  optimizationObjective?: string
  status?: string
  isActive?: boolean
  // Extended vehicle fields - Phase 2A Integration
  profile?: string
  reference?: string
  compartments?: Compartment[]
  capacity?: number[] // Generated from compartments - hidden from user
  startLocationId?: Id<'locations'>
  endLocationId?: Id<'locations'>
  startLon?: number
  startLat?: number
  endLon?: number
  endLat?: number
  timeWindow?: number[]
  maxTravelTime?: number
  maxDistance?: number
  costFixed?: number
  costPerHour?: number
  costPerKm?: number
  // Extended job fields - Phase 2B Integration
  locationId?: Id<'locations'>
  locationLon?: number
  locationLat?: number
  setup?: number
  service?: number
  deliveryItems?: DeliveryItem[]
  pickupItems?: PickupItem[]
  delivery?: number[] // Auto-generated from deliveryItems - hidden from user
  pickup?: number[] // Auto-generated from pickupItems - hidden from user
  skills?: number[]
  priority?: number
  timeWindow?: {
    windows: number[][]
    format: 'relative' | 'absolute'
    timezone?: string
    planningHorizonStart?: number
  }
  selectedVehicleId?: Id<'vehicles'> // For validation
  // Support context for hooks
  projectId?: Id<'projects'>
  scenarioId?: Id<'scenarios'>
  datasetId?: Id<'datasets'>
}

export interface EditModalProps {
  isOpen: boolean
  onClose: () => void
  data: EditModalData | null
  onSave: (updatedData: Partial<EditModalData>) => Promise<void>
  isLoading?: boolean
  error?: string | null
}

// Enhanced timezone options with search functionality
const TIMEZONE_OPTIONS: SearchableSelectOption[] = [
  {
    value: 'Africa/Johannesburg',
    label: 'South Africa (Johannesburg)',
    keywords: ['south africa', 'johannesburg', 'sast', 'cst', 'cat', 'utc+2'],
  },
  {
    value: 'UTC',
    label: 'UTC (Coordinated Universal Time)',
    keywords: [
      'utc',
      'gmt',
      'universal',
      'greenwich',
      'zulu',
      'coordinated',
      'utc+0',
    ],
  },
  {
    value: 'Europe/London',
    label: 'United Kingdom (London)',
    keywords: [
      'uk',
      'london',
      'britain',
      'england',
      'gmt',
      'bst',
      'utc+0',
      'utc+1',
    ],
  },
  {
    value: 'Europe/Paris',
    label: 'France (Paris)',
    keywords: ['france', 'paris', 'cet', 'cest', 'utc+1', 'utc+2'],
  },
  {
    value: 'Europe/Berlin',
    label: 'Germany (Berlin)',
    keywords: ['germany', 'berlin', 'cet', 'cest', 'utc+1', 'utc+2'],
  },
  {
    value: 'Europe/Madrid',
    label: 'Spain (Madrid)',
    keywords: ['spain', 'madrid', 'cet', 'cest', 'utc+1', 'utc+2'],
  },
  {
    value: 'Europe/Amsterdam',
    label: 'Netherlands (Amsterdam)',
    keywords: [
      'netherlands',
      'amsterdam',
      'holland',
      'cet',
      'cest',
      'utc+1',
      'utc+2',
    ],
  },
  {
    value: 'Europe/Moscow',
    label: 'Russia (Moscow)',
    keywords: ['russia', 'moscow', 'msk', 'utc+3'],
  },
  {
    value: 'America/New_York',
    label: 'US Eastern (New York)',
    keywords: [
      'eastern',
      'est',
      'edt',
      'new york',
      'usa',
      'us east coast',
      'utc-5',
      'utc-4',
    ],
  },
  {
    value: 'America/Chicago',
    label: 'US Central (Chicago)',
    keywords: [
      'central',
      'cst',
      'cdt',
      'chicago',
      'usa',
      'midwest',
      'utc-6',
      'utc-5',
    ],
  },
  {
    value: 'America/Denver',
    label: 'US Mountain (Denver)',
    keywords: [
      'mountain',
      'mst',
      'mdt',
      'denver',
      'usa',
      'colorado',
      'utc-7',
      'utc-6',
    ],
  },
  {
    value: 'America/Los_Angeles',
    label: 'US Pacific (Los Angeles)',
    keywords: [
      'pacific',
      'pst',
      'pdt',
      'los angeles',
      'california',
      'usa',
      'west coast',
      'utc-8',
      'utc-7',
    ],
  },
  {
    value: 'America/Phoenix',
    label: 'US Arizona (Phoenix)',
    keywords: ['arizona', 'phoenix', 'mst', 'usa', 'utc-7'],
  },
  {
    value: 'America/Toronto',
    label: 'Canada Eastern (Toronto)',
    keywords: ['canada', 'toronto', 'eastern', 'est', 'edt', 'utc-5', 'utc-4'],
  },
  {
    value: 'America/Vancouver',
    label: 'Canada Pacific (Vancouver)',
    keywords: [
      'canada',
      'vancouver',
      'pacific',
      'pst',
      'pdt',
      'utc-8',
      'utc-7',
    ],
  },
  {
    value: 'America/Sao_Paulo',
    label: 'Brazil (São Paulo)',
    keywords: ['brazil', 'sao paulo', 'brt', 'brst', 'utc-3', 'utc-2'],
  },
  {
    value: 'Asia/Tokyo',
    label: 'Japan (Tokyo)',
    keywords: ['japan', 'tokyo', 'jst', 'utc+9'],
  },
  {
    value: 'Asia/Shanghai',
    label: 'China (Shanghai)',
    keywords: ['china', 'shanghai', 'beijing', 'cst', 'utc+8'],
  },
  {
    value: 'Asia/Hong_Kong',
    label: 'Hong Kong',
    keywords: ['hong kong', 'hkt', 'utc+8'],
  },
  {
    value: 'Asia/Singapore',
    label: 'Singapore',
    keywords: ['singapore', 'sgt', 'utc+8'],
  },
  {
    value: 'Asia/Seoul',
    label: 'South Korea (Seoul)',
    keywords: ['korea', 'seoul', 'kst', 'utc+9'],
  },
  {
    value: 'Asia/Mumbai',
    label: 'India (Mumbai)',
    keywords: ['india', 'mumbai', 'bombay', 'ist', 'utc+5:30'],
  },
  {
    value: 'Asia/Kolkata',
    label: 'India (Kolkata)',
    keywords: ['india', 'kolkata', 'calcutta', 'ist', 'utc+5:30'],
  },
  {
    value: 'Asia/Bangkok',
    label: 'Thailand (Bangkok)',
    keywords: ['thailand', 'bangkok', 'ict', 'utc+7'],
  },
  {
    value: 'Asia/Dubai',
    label: 'UAE (Dubai)',
    keywords: ['uae', 'dubai', 'emirates', 'gst', 'utc+4'],
  },
  {
    value: 'Australia/Sydney',
    label: 'Australia Eastern (Sydney)',
    keywords: ['australia', 'sydney', 'aest', 'aedt', 'utc+10', 'utc+11'],
  },
  {
    value: 'Australia/Melbourne',
    label: 'Australia Eastern (Melbourne)',
    keywords: ['australia', 'melbourne', 'aest', 'aedt', 'utc+10', 'utc+11'],
  },
  {
    value: 'Australia/Perth',
    label: 'Australia Western (Perth)',
    keywords: ['australia', 'perth', 'awst', 'utc+8'],
  },
  {
    value: 'Pacific/Auckland',
    label: 'New Zealand (Auckland)',
    keywords: ['new zealand', 'auckland', 'nzst', 'nzdt', 'utc+12', 'utc+13'],
  },
  {
    value: 'Pacific/Honolulu',
    label: 'Hawaii (Honolulu)',
    keywords: ['hawaii', 'honolulu', 'hst', 'utc-10'],
  },
]

// Field configurations for different entity types
const FIELD_CONFIGURATIONS: Record<string, FieldConfig[]> = {
  project: [
    // Basic Information
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      group: 'basic',
      placeholder: 'Enter project name',
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      group: 'basic',
      placeholder: 'Enter project description',
    },

    // Business Information
    {
      key: 'currency',
      label: 'Currency',
      type: 'select',
      group: 'business',
      options: [
        { value: 'USD', label: 'USD - US Dollar' },
        { value: 'EUR', label: 'EUR - Euro' },
        { value: 'GBP', label: 'GBP - British Pound' },
        { value: 'CAD', label: 'CAD - Canadian Dollar' },
        { value: 'AUD', label: 'AUD - Australian Dollar' },
        { value: 'JPY', label: 'JPY - Japanese Yen' },
        { value: 'ZAR', label: 'ZAR - South African Rand' },
      ],
    },
    {
      key: 'projectType',
      label: 'Project Type',
      type: 'select',
      group: 'business',
      options: [
        { value: 'delivery', label: 'Delivery' },
        { value: 'service', label: 'Service' },
        { value: 'logistics', label: 'Logistics' },
        { value: 'distribution', label: 'Distribution' },
        { value: 'maintenance', label: 'Maintenance' },
        { value: 'custom', label: 'Custom' },
      ],
    },
    {
      key: 'industry',
      label: 'Industry',
      type: 'select',
      group: 'business',
      options: [
        { value: 'retail', label: 'Retail' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'food-beverage', label: 'Food & Beverage' },
        { value: 'automotive', label: 'Automotive' },
        { value: 'construction', label: 'Construction' },
        { value: 'technology', label: 'Technology' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      key: 'geographicRegion',
      label: 'Geographic Region',
      type: 'text',
      group: 'business',
      placeholder: 'e.g., North America, Europe',
    },
    {
      key: 'priority',
      label: 'Priority',
      type: 'select',
      group: 'business',
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' },
      ],
    },

    // Planning Information
    {
      key: 'estimatedVehicles',
      label: 'Estimated Vehicles',
      type: 'number',
      group: 'planning',
      min: 0,
      placeholder: 'Expected number of vehicles',
    },
    {
      key: 'estimatedJobs',
      label: 'Estimated Jobs',
      type: 'number',
      group: 'planning',
      min: 0,
      placeholder: 'Expected number of jobs',
    },
    {
      key: 'planningHorizonDays',
      label: 'Planning Horizon (Days)',
      type: 'number',
      group: 'planning',
      min: 1,
      placeholder: 'Planning period length',
    },
    {
      key: 'timezone',
      label: 'Project Timezone',
      type: 'searchable_select',
      group: 'planning',
      options: TIMEZONE_OPTIONS,
      placeholder: 'Search and select timezone...',
      searchPlaceholder:
        'Search timezones (e.g. "EST", "Pacific", "London")...',
      emptyMessage: 'No timezones found',
      maxHeight: 300,
    },

    // Contact Information
    {
      key: 'contactPerson',
      label: 'Contact Person',
      type: 'text',
      group: 'contact',
      placeholder: 'Primary contact name',
    },
    {
      key: 'contactEmail',
      label: 'Contact Email',
      type: 'email',
      group: 'contact',
      placeholder: 'contact@example.com',
    },
    {
      key: 'contactPhone',
      label: 'Contact Phone',
      type: 'tel',
      group: 'contact',
      placeholder: '+1 (555) 123-4567',
    },

    // Metadata
    {
      key: 'notes',
      label: 'Notes',
      type: 'textarea',
      group: 'metadata',
      placeholder: 'Additional project notes',
    },
  ],

  scenario: [
    // Basic Information
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      group: 'basic',
      placeholder: 'Enter scenario name',
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      group: 'basic',
      placeholder: 'Enter scenario description',
    },

    // Scheduling
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date',
      group: 'scheduling',
    },
    { key: 'endDate', label: 'End Date', type: 'date', group: 'scheduling' },
    {
      key: 'planningHorizonDays',
      label: 'Planning Horizon (Days)',
      type: 'number',
      group: 'scheduling',
      min: 1,
      placeholder: 'Override project default',
    },

    // Optimization
    {
      key: 'optimizationObjective',
      label: 'Optimization Objective',
      type: 'select',
      group: 'optimization',
      options: [
        { value: 'minimize-cost', label: 'Minimize Cost' },
        { value: 'minimize-distance', label: 'Minimize Distance' },
        { value: 'minimize-time', label: 'Minimize Time' },
        { value: 'maximize-efficiency', label: 'Maximize Efficiency' },
        { value: 'balance-workload', label: 'Balance Workload' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      group: 'optimization',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      key: 'isActive',
      label: 'Active Scenario',
      type: 'boolean',
      group: 'optimization',
      helpText: 'Mark this as the active scenario',
    },
  ],
  // Phase 2A: Vehicle Configuration with CompartmentBuilder
  vehicle: [
    // Basic Vehicle Information
    {
      key: 'description',
      label: 'Vehicle Description',
      type: 'text',
      required: true,
      group: 'basic',
      placeholder: 'e.g., Fuel Tanker #1, Delivery Van A',
      helpText: 'Descriptive name for this vehicle',
    },
    {
      key: 'profile',
      label: 'Vehicle Profile',
      type: 'select',
      required: true,
      group: 'basic',
      options: [
        { value: 'car', label: 'Car' },
        { value: 'truck', label: 'Truck' },
        { value: 'van', label: 'Van' },
        { value: 'bike', label: 'Bike' },
        { value: 'walking', label: 'Walking' },
      ],
      helpText: 'Vehicle type for routing calculations',
    },
    {
      key: 'reference',
      label: 'External Reference',
      type: 'text',
      group: 'basic',
      placeholder: 'Fleet ID, License Plate, etc.',
      helpText: 'Reference ID from your fleet management system',
    },

    // Compartment Configuration (NEW - Main focus of Phase 2A)
    {
      key: 'compartments',
      label: 'Vehicle Compartments',
      type: 'compartments',
      required: true,
      group: 'compartments',
      helpText: '🚛 Configure compartments instead of raw capacity arrays',
    },

    // Location Configuration
    {
      key: 'startLocationId',
      label: 'Start Location',
      type: 'location-select',
      group: 'location',
      helpText: 'Where this vehicle begins its route',
    },
    {
      key: 'endLocationId',
      label: 'End Location',
      type: 'location-select',
      group: 'location',
      helpText: 'Where this vehicle ends its route',
    },

    // Time and Distance Constraints
    {
      key: 'maxTravelTime',
      label: 'Max Travel Time (seconds)',
      type: 'number',
      group: 'constraints',
      placeholder: '28800 (8 hours)',
      min: 0,
      helpText: 'Maximum time this vehicle can travel',
    },
    {
      key: 'maxDistance',
      label: 'Max Distance (meters)',
      type: 'number',
      group: 'constraints',
      placeholder: '500000 (500km)',
      min: 0,
      helpText: 'Maximum distance this vehicle can travel',
    },

    // Cost Structure
    {
      key: 'costFixed',
      label: 'Fixed Cost',
      type: 'number',
      group: 'costs',
      placeholder: '0.00',
      min: 0,
      helpText: 'Fixed cost per vehicle usage',
    },
    {
      key: 'costPerHour',
      label: 'Cost Per Hour',
      type: 'number',
      group: 'costs',
      placeholder: '0.00',
      min: 0,
      helpText: 'Hourly operating cost',
    },
    {
      key: 'costPerKm',
      label: 'Cost Per Kilometer',
      type: 'number',
      group: 'costs',
      placeholder: '0.00',
      min: 0,
      helpText: 'Distance-based cost',
    },
  ],

  // Phase 2B: Job Configuration with Product-Driven Interface
  job: [
    // Basic Job Information
    {
      key: 'description',
      label: 'Job Description',
      type: 'text',
      required: true,
      group: 'basic',
      placeholder:
        'e.g., Deliver fuel to Station A, Pickup parcels from Depot B',
      helpText: 'Descriptive name for this job/task',
    },
    {
      key: 'reference',
      label: 'External Reference',
      type: 'text',
      group: 'basic',
      placeholder: 'Order ID, Ticket #, etc.',
      helpText: 'Reference ID from your order management system',
    },
    {
      key: 'priority',
      label: 'Job Priority',
      type: 'number',
      group: 'basic',
      placeholder: '50 (0-100)',
      min: 0,
      max: 100,
      helpText: 'Priority level (0 = lowest, 100 = highest)',
    },

    // Location Configuration
    {
      key: 'locationId',
      label: 'Job Location',
      type: 'location-select',
      group: 'location',
      helpText: 'Where this job takes place',
    },

    // Product-Based Capacity (NEW - Main focus of Phase 2B)
    {
      key: 'deliveryItems',
      label: 'Delivery Items',
      type: 'delivery-items',
      group: 'items',
      helpText: '📦 Select products and quantities to deliver',
    },
    {
      key: 'pickupItems',
      label: 'Pickup Items',
      type: 'pickup-items',
      group: 'items',
      helpText: '📦 Select products and quantities to pickup',
    },

    // Vehicle Selection for Validation
    {
      key: 'selectedVehicleId',
      label: 'Preferred Vehicle',
      type: 'vehicle-select',
      group: 'validation',
      helpText: '🚛 Select vehicle to validate capacity compatibility',
    },

    // Time Requirements
    {
      key: 'setup',
      label: 'Setup Time',
      type: 'duration',
      group: 'timing',
      placeholder: 'e.g., 15 minutes',
      min: 0,
      helpText: 'Time needed to prepare before starting service',
    },
    {
      key: 'service',
      label: 'Service Time',
      type: 'duration',
      group: 'timing',
      placeholder: 'e.g., 30 minutes',
      min: 0,
      helpText: 'Time needed to complete the actual service',
    },

    // Skills and Constraints
    {
      key: 'skills',
      label: 'Required Skills',
      type: 'skills-select',
      group: 'constraints',
      helpText: 'Skills that the assigned vehicle/driver must have',
    },
  ],
}

/**
 * EditModal - Reusable modal for editing project, scenario, and dataset entities
 * Following shadcn/ui v4 patterns with proper accessibility and design system compliance
 */
export const EditModal = ({
  isOpen,
  onClose,
  data,
  onSave,
  isLoading = false,
  error = null,
}: EditModalProps) => {
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({
    basic: false,
    business: true,
    planning: true,
    contact: true,
    metadata: true,
    scheduling: true,
    optimization: true,
    // Phase 2A: Vehicle groups - compartments prominent
    compartments: false, // Always show compartments by default
    location: true,
    constraints: true,
    costs: true,
    // Phase 2B: Job groups - items prominent
    items: false, // Always show items by default for jobs
    timing: true,
    validation: true,
  })

  // Phase 2A: Vehicle-specific state for CompartmentBuilder integration
  const [generatedCapacityArray, setGeneratedCapacityArray] = useState<
    number[]
  >([])
  const [showCapacityPreview, setShowCapacityPreview] = useState(false)

  // Phase 2A: Vehicle-specific hooks with defensive loading
  const products = useProducts(data?.projectId)
  const locations = useLocationsByProject(data?.projectId)

  // Enhanced debug logging for products integration
  useEffect(() => {
    if (data?.projectId && products !== undefined) {
      console.log('🔍 Products Integration Debug:', {
        projectId: data.projectId,
        productsCount: products?.length || 0,
        products: products,
        hasProducts: Array.isArray(products) && products.length > 0,
        componentType: data?.type,
      })
    }
  }, [data?.projectId, products, data?.type])
  const generateCapacityArray = useGenerateCapacityArray()

  // Phase 2B: Job-specific hooks with defensive loading
  const vehicles = useVehiclesByProject(data?.projectId)
  const skills = useSkills(data?.projectId)
  const calculateJobCapacityArrays = useCalculateJobCapacityArrays()
  const validateJobAgainstVehicle = useValidateJobAgainstVehicle()
  const { formatDuration } = useTimeFormat()

  // Error boundary for capacity operations
  const errorBoundary = useErrorBoundary()

  // Phase 2B: Job-specific state
  const [jobCapacityValidation, setJobCapacityValidation] = useState<{
    isValid: boolean
    violations: string[]
    capacityArray?: number[]
  } | null>(null)

  // Get field configuration for current entity type
  const fieldConfigs = data ? FIELD_CONFIGURATIONS[data.type] || [] : []

  // Group fields by their group property
  const groupedFields = fieldConfigs.reduce(
    (groups, field) => {
      const group = field.group || 'basic'
      if (!groups[group]) {
        groups[group] = []
      }
      groups[group].push(field)
      return groups
    },
    {} as Record<string, FieldConfig[]>
  )

  // Reset form when modal opens with new data
  useEffect(() => {
    if (data) {
      const initialFormData: Record<string, any> = {}

      // Initialize all fields from configuration
      fieldConfigs.forEach(field => {
        const value = (data as any)[field.key]
        if (field.type === 'boolean') {
          initialFormData[field.key] = Boolean(value)
        } else if (field.type === 'number') {
          initialFormData[field.key] = value || 0
        } else if (field.type === 'multi-select') {
          initialFormData[field.key] = value || []
        } else if (field.type === 'date') {
          initialFormData[field.key] = value
            ? new Date(value).toISOString().split('T')[0]
            : ''
        } else {
          initialFormData[field.key] = value || ''
        }
      })

      setFormData(initialFormData)
      setValidationErrors({})
    }
  }, [data, fieldConfigs])

  // Dynamic form validation based on field configurations
  const validateForm = () => {
    const errors: Record<string, string> = {}

    fieldConfigs.forEach(field => {
      const value = formData[field.key]

      // Required field validation
      if (field.required) {
        if (field.type === 'boolean') {
          // Booleans don't need required validation typically
        } else if (field.type === 'number') {
          if (value === null || value === undefined || value === '') {
            errors[field.key] = `${field.label} is required`
          }
        } else if (field.type === 'multi-select') {
          if (!value || !Array.isArray(value) || value.length === 0) {
            errors[field.key] = `${field.label} is required`
          }
        } else if (!value || (typeof value === 'string' && !value.trim())) {
          errors[field.key] = `${field.label} is required`
        }
      }

      // Type-specific validation
      if (value) {
        switch (field.type) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (typeof value === 'string' && !emailRegex.test(value)) {
              errors[field.key] = 'Please enter a valid email address'
            }
            break
          case 'tel':
            // Basic phone validation - could be enhanced
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
            if (
              typeof value === 'string' &&
              value.replace(/[\s\-\(\)]/g, '').length > 0
            ) {
              const cleanPhone = value.replace(/[\s\-\(\)]/g, '')
              if (!phoneRegex.test(cleanPhone)) {
                errors[field.key] = 'Please enter a valid phone number'
              }
            }
            break
          case 'number':
            if (field.min !== undefined && value < field.min) {
              errors[field.key] = `${field.label} must be at least ${field.min}`
            }
            if (field.max !== undefined && value > field.max) {
              errors[field.key] =
                `${field.label} must be no more than ${field.max}`
            }
            break
          case 'text':
          case 'textarea':
            if (typeof value === 'string' && value.length > 1000) {
              errors[field.key] =
                `${field.label} must be 1000 characters or less`
            }
            break
        }
      }
    })

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle field value changes
  const handleFieldChange = (fieldKey: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }))

    // Clear validation error when field is changed
    if (validationErrors[fieldKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[fieldKey]
        return newErrors
      })
    }
  }

  // Phase 2B: Handle job capacity validation
  const handleJobItemsValidation = useCallback(
    async (
      deliveryItems: DeliveryItem[],
      pickupItems: PickupItem[],
      selectedVehicleId?: string
    ) => {
      // Defensive checks for required data
      if (
        !products ||
        !Array.isArray(products) ||
        (!deliveryItems?.length && !pickupItems?.length)
      ) {
        setJobCapacityValidation(null)
        return
      }

      try {
        // Calculate job capacity requirements with error boundary
        const result = await errorBoundary.withErrorBoundary(
          calculateJobCapacityArrays
        )({
          deliveryItems: deliveryItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
          })),
          pickupItems: pickupItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
          })),
          products: products.map(p => ({
            _id: p._id,
            weightPerUnitKg: p.weightPerUnitKg || 0,
            volumePerUnitM3: p.volumePerUnitM3 || 0,
            unitType: p.unitType || 'item',
          })),
        })

        // Handle error boundary result
        if (!result) {
          if (errorBoundary.hasError) {
            toast.error('Job capacity calculation failed due to an error')
            return
          }
        }

        if (
          result &&
          result.success &&
          result.deliveryArray &&
          selectedVehicleId &&
          vehicles
        ) {
          // Validate against selected vehicle
          const vehicle = vehicles.find(v => v._id === selectedVehicleId)
          if (vehicle) {
            const validation = await validateJobAgainstVehicle({
              jobCapacity: result.deliveryArray,
              vehicleId: selectedVehicleId as Id<'vehicles'>,
            })

            setJobCapacityValidation({
              isValid: validation.success && !validation.violations?.length,
              violations: validation.violations || [],
              capacityArray: result.deliveryArray,
            })
          }
        } else if (result.success) {
          setJobCapacityValidation({
            isValid: true,
            violations: [],
            capacityArray: result.deliveryArray,
          })
        }
      } catch (error) {
        console.error('Job capacity validation error:', error)
        setJobCapacityValidation({
          isValid: false,
          violations: ['Failed to calculate job capacity requirements'],
          capacityArray: undefined,
        })
      }
    },
    [products, vehicles, calculateJobCapacityArrays, validateJobAgainstVehicle]
  )

  // Phase 2A: Handle compartment changes with proper backend-first approach
  const handleCompartmentsChange = React.useCallback(
    async (newCompartments: Compartment[]) => {
      // Defensive checks for compartments data
      if (!Array.isArray(newCompartments)) {
        console.warn('Invalid compartments data received:', newCompartments)
        return
      }

      // Update form data
      setFormData(prev => ({ ...prev, compartments: newCompartments }))

      // Clear compartment validation errors
      if (validationErrors.compartments) {
        setValidationErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors.compartments
          return newErrors
        })
      }

      // Generate capacity array if compartments exist
      if (newCompartments.length === 0) {
        setGeneratedCapacityArray([])
        setShowCapacityPreview(false)
        return
      }

      try {
        // Check if we're creating a new vehicle (no ID) or editing an existing one
        if (!data?._id || data?.type !== 'vehicle') {
          // For NEW vehicles: Simply collect compartment data
          // Don't attempt capacity generation (no vehicle ID exists yet)
          // No capacity preview during creation
          setShowCapacityPreview(false)
          setGeneratedCapacityArray([])

          // Note: Capacity will be generated by backend after vehicle is saved
          console.log(
            'Vehicle creation: compartments collected, capacity generation after save'
          )
          return
        }

        // For EXISTING vehicles: Use backend generateCapacityArray mutation
        const result = await errorBoundary.withErrorBoundary(
          generateCapacityArray
        )({
          vehicleId: data._id as Id<'vehicles'>,
          compartments: convertCompartmentsToBackendSchema(newCompartments),
          availableProducts: filterProductsForBackend(products || []),
        })

        // Handle error boundary result
        if (!result) {
          if (errorBoundary.hasError) {
            toast.error('Capacity generation failed due to an error')
            return
          }
        }

        if (result && result.success && result.capacityArray) {
          setGeneratedCapacityArray(result.capacityArray)
          setShowCapacityPreview(true)

          // Automatically update capacity in form data (hidden from user)
          setFormData(prev => ({ ...prev, capacity: result.capacityArray }))

          toast.success('✨ Vehicle capacity calculated from compartments')
        } else {
          toast.error('Failed to generate capacity array from compartments')
        }
      } catch (error) {
        console.error('Capacity generation error:', error)

        // Provide specific error messages based on error type
        let errorMessage =
          'Error calculating vehicle capacity. Please check compartment configuration.'

        if (error instanceof Error) {
          if (error.message.includes('ArgumentValidationError')) {
            // Extract specific validation error details
            const validationMatch = error.message.match(
              /Object contains extra field `(\w+)`/
            )
            if (validationMatch) {
              errorMessage = `Invalid compartment field: ${validationMatch[1]}. Please check compartment configuration.`
            } else {
              errorMessage = `Compartment validation error: ${error.message.split('ArgumentValidationError: ')[1]?.split('\n')[0] || 'Invalid compartment data'}`
            }
          } else if (error.message.includes('vehicleId')) {
            errorMessage =
              'Vehicle ID is required for capacity calculation. Please save the vehicle first.'
          } else {
            // Show actual error message for debugging
            errorMessage = `Capacity generation failed: ${error.message}`
          }
        }

        toast.error(errorMessage)
      }
    },
    [generateCapacityArray, products, validationErrors.compartments]
  )

  // Available products for compartment configuration (vehicles) and delivery items (jobs)
  const availableProducts = React.useMemo(() => {
    // Products are needed for both vehicles (compartments) and jobs (delivery/pickup items)
    if (!products || !data?.projectId) return []
    if (data?.type !== 'vehicle' && data?.type !== 'job') return []

    return products
  }, [products, data?.type, data?.projectId])

  const handleSave = async () => {
    if (!data || !validateForm()) return

    try {
      // Prepare save data - convert form values back to proper types
      const saveData: any = { id: data._id }

      fieldConfigs.forEach(field => {
        const value = formData[field.key]

        if (field.type === 'date' && value) {
          // Convert date string back to timestamp
          saveData[field.key] = new Date(value).getTime()
        } else if (field.type === 'number') {
          saveData[field.key] = value === '' ? undefined : Number(value)
        } else if (
          field.type === 'text' ||
          field.type === 'textarea' ||
          field.type === 'email' ||
          field.type === 'tel'
        ) {
          saveData[field.key] = value ? value.trim() : undefined
        } else {
          saveData[field.key] = value === '' ? undefined : value
        }
      })

      await onSave(saveData)
      onClose()
    } catch (error) {
      // Error handling is managed by parent component
      console.error('Save failed:', error)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSave()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  // Toggle group collapse state
  const toggleGroup = (groupName: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }))
  }

  // Render individual field based on its configuration
  const renderField = (field: FieldConfig) => {
    const value = formData[field.key] || ''
    const error = validationErrors[field.key]
    const fieldId = `field-${field.key}`

    switch (field.type) {
      case 'select':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <Select
              value={value}
              onValueChange={newValue => handleFieldChange(field.key, newValue)}
              disabled={isLoading}
            >
              <SelectTrigger className={error ? 'border-destructive' : ''}>
                <SelectValue
                  placeholder={
                    field.placeholder || `Select ${field.label.toLowerCase()}`
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      case 'searchable_select':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <SearchableSelect
              value={value}
              onValueChange={newValue => handleFieldChange(field.key, newValue)}
              options={field.options as SearchableSelectOption[]}
              placeholder={
                field.placeholder || `Search ${field.label.toLowerCase()}...`
              }
              searchPlaceholder={field.searchPlaceholder || 'Search...'}
              emptyMessage={field.emptyMessage || 'No options found'}
              maxHeight={field.maxHeight || 200}
              disabled={isLoading}
              className={error ? 'border-destructive' : ''}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      case 'boolean':
        return (
          <div key={field.key} className="flex items-center space-x-2">
            <Switch
              id={fieldId}
              checked={Boolean(value)}
              onCheckedChange={checked => handleFieldChange(field.key, checked)}
              disabled={isLoading}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor={fieldId}
                className="text-sm font-normal cursor-pointer"
              >
                {field.label}
              </Label>
              {field.helpText && (
                <p className="text-sm text-muted-foreground">
                  {field.helpText}
                </p>
              )}
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      case 'number':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <Input
              id={fieldId}
              type="number"
              value={value}
              onChange={e =>
                handleFieldChange(
                  field.key,
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              placeholder={field.placeholder}
              disabled={isLoading}
              className={error ? 'border-destructive' : ''}
              min={field.min}
              max={field.max}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      case 'date':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <div className="relative">
              <Input
                id={fieldId}
                type="date"
                value={value}
                onChange={e => handleFieldChange(field.key, e.target.value)}
                disabled={isLoading}
                className={error ? 'border-destructive' : ''}
              />
              <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      case 'textarea':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <Textarea
              id={fieldId}
              value={value}
              onChange={e => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              disabled={isLoading}
              className={error ? 'border-destructive' : ''}
              rows={3}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      // Phase 2A: CompartmentBuilder field type
      case 'compartments':
        return (
          <div key={field.key} className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-normal">
                {field.label} {field.required && '*'}
              </Label>
              {showCapacityPreview && (
                <Badge variant="secondary" className="text-sm">
                  ✨ Auto-generated capacity
                </Badge>
              )}
            </div>

            <CapacityErrorBoundary
              context="Compartment Builder"
              onError={error => {
                toast.error('Compartment configuration error occurred')
                console.error('CompartmentBuilder error:', error)
              }}
            >
              <CompartmentBuilder
                value={
                  Array.isArray(formData.compartments)
                    ? formData.compartments
                    : []
                }
                onChange={handleCompartmentsChange}
                availableProducts={availableProducts || []}
                maxCompartments={10}
                enableDragAndDrop={true}
                showAdvancedFeatures={true}
                enableCapacityPreview={true}
                disabled={isLoading || errorBoundary.hasError}
                className={
                  error ? 'border border-destructive rounded-md p-1' : ''
                }
              />
            </CapacityErrorBoundary>

            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}

            {/* Capacity Preview - Only shown for existing vehicles after backend calculation */}
            {showCapacityPreview &&
              generatedCapacityArray.length > 0 &&
              data?.id && (
                <div className="mt-3 p-3 bg-muted/20 rounded-md border">
                  <Label className="text-sm font-normal mb-2 block">
                    Generated Capacity Preview
                  </Label>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="font-normal text-sm">Weight</div>
                      <div>{generatedCapacityArray[0] || 0} kg</div>
                    </div>
                    <div>
                      <div className="font-normal text-sm">Volume</div>
                      <div>
                        {(generatedCapacityArray[1] || 0).toFixed(2)} m³
                      </div>
                    </div>
                    <div>
                      <div className="font-normal text-sm">Items</div>
                      <div>{generatedCapacityArray[2] || 0} items</div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="text-sm text-muted-foreground font-mono">
                      Raw: [{generatedCapacityArray.join(', ')}]
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      🎯 Users see compartments, optimization uses arrays
                    </p>
                  </div>
                </div>
              )}

            {/* Info for new vehicle creation */}
            {(!data?._id || data?.type !== 'vehicle') &&
              formData.compartments?.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <div className="text-sm text-blue-800">
                    💡 <strong>Vehicle Creation:</strong> Capacity will be
                    calculated automatically after saving the vehicle.
                  </div>
                </div>
              )}

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      // Phase 2A: LocationSelect field type
      case 'location-select':
        return (
          <div key={field.key} className="space-y-2">
            <Label className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <LocationSelect
              locations={locations || []}
              value={formData[field.key]}
              onValueChange={locationId =>
                handleFieldChange(field.key, locationId)
              }
              placeholder={
                field.placeholder || `Select ${field.label.toLowerCase()}...`
              }
              disabled={isLoading}
              isLoading={!locations}
              className={error ? 'border-destructive' : ''}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      // Phase 2B: DeliveryItemsManager field type
      case 'delivery-items':
        return (
          <div key={field.key} className="space-y-2">
            <Label className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <DeliveryItemsManager
              value={formData.deliveryItems || []}
              onChange={items => {
                handleFieldChange('deliveryItems', items)
                // Auto-validate capacity when items change
                handleJobItemsValidation(
                  items,
                  formData.pickupItems || [],
                  formData.selectedVehicleId
                )
              }}
              projectId={data?.projectId || ''}
              products={availableProducts || []}
              availableVehicles={vehicles || []}
              selectedVehicleId={formData.selectedVehicleId}
              disabled={isLoading}
              showCapacityWarnings={true}
              isLoading={products === undefined}
              error={
                products === null
                  ? 'Failed to load products from database'
                  : null
              }
              onRetryLoadProducts={() => {
                // This would trigger a refetch - implementation depends on parent component
                console.log('Retry loading products requested')
                toast.error(
                  'Retry functionality needs to be implemented by parent component'
                )
              }}
              onCreateProduct={() => {
                // This would open product creation modal - implementation depends on parent component
                console.log('Create product requested')
                toast.info(
                  'Product creation functionality needs to be implemented by parent component'
                )
              }}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      // Phase 2B: PickupItemsManager field type
      case 'pickup-items':
        return (
          <div key={field.key} className="space-y-2">
            <Label className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <PickupItemsManager
              value={formData.pickupItems || []}
              onChange={items => {
                handleFieldChange('pickupItems', items)
                // Auto-validate capacity when items change
                handleJobItemsValidation(
                  formData.deliveryItems || [],
                  items,
                  formData.selectedVehicleId
                )
              }}
              projectId={data?.projectId || ''}
              products={availableProducts || []}
              availableVehicles={vehicles || []}
              selectedVehicleId={formData.selectedVehicleId}
              disabled={isLoading}
              showCapacityWarnings={true}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      // Phase 2B: Vehicle selection for job validation
      case 'vehicle-select':
        return (
          <div key={field.key} className="space-y-2">
            <Label className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <Select
              value={formData[field.key] || ''}
              onValueChange={vehicleId => {
                handleFieldChange(field.key, vehicleId)
                // Auto-validate when vehicle selection changes
                handleJobItemsValidation(
                  formData.deliveryItems || [],
                  formData.pickupItems || [],
                  vehicleId
                )
              }}
              disabled={isLoading}
            >
              <SelectTrigger className={error ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select vehicle for validation..." />
              </SelectTrigger>
              <SelectContent>
                {vehicles && Array.isArray(vehicles) ? (
                  vehicles.map(vehicle => (
                    <SelectItem key={vehicle._id} value={vehicle._id}>
                      <div className="flex items-center gap-2">
                        <span>
                          {vehicle.description ||
                            `Vehicle ${vehicle._id.slice(-8)}`}
                        </span>
                        {vehicle.profile && (
                          <Badge variant="outline" className="text-sm">
                            {vehicle.profile}
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="" disabled>
                    No vehicles available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      // Phase 2B: Duration input field (converts user-friendly input to seconds)
      case 'duration':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <Input
              id={fieldId}
              type="number"
              value={value ? Math.floor(value / 60) : ''} // Convert seconds to minutes for user
              onChange={e => {
                const minutes = parseInt(e.target.value) || 0
                handleFieldChange(field.key, minutes * 60) // Convert back to seconds
              }}
              placeholder="Enter minutes"
              disabled={isLoading}
              className={error ? 'border-destructive' : ''}
              min={0}
            />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Minutes</span>
              {value && <span>({formatDuration(value)})</span>}
            </div>
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )

      // Phase 2B: Skills selection (simplified for now)
      case 'skills-select':
        return (
          <div key={field.key} className="space-y-2">
            <Label className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <div className="text-sm text-muted-foreground p-3 border rounded-md bg-muted/10">
              Skills selection coming in future phase
              {skills && (
                <div className="mt-2">Available skills: {skills.length}</div>
              )}
            </div>
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
          </div>
        )

      default: // text, email, tel
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={fieldId} className="text-sm font-normal">
              {field.label} {field.required && '*'}
            </Label>
            <Input
              id={fieldId}
              type={field.type}
              value={value}
              onChange={e => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              disabled={isLoading}
              className={error ? 'border-destructive' : ''}
            />
            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )
    }
  }

  // Group display names
  const GROUP_DISPLAY_NAMES: Record<string, string> = {
    basic: 'Basic Information',
    business: 'Business Information',
    planning: 'Planning Information',
    contact: 'Contact Information',
    metadata: 'Additional Information',
    scheduling: 'Scheduling',
    optimization: 'Optimization Settings',
    // Phase 2A: Vehicle groups
    compartments: '🚛 Vehicle Compartments',
    location: '📍 Location Configuration',
    constraints: '⏱️ Time & Distance Limits',
    costs: '💰 Cost Structure',
    // Phase 2B: Job groups
    items: '📦 Delivery & Pickup Items',
    timing: '⏰ Time Requirements',
    validation: '🔍 Vehicle Validation',
  }

  if (!data) return null

  const entityTypeDisplayName =
    data.type.charAt(0).toUpperCase() + data.type.slice(1)

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Edit {entityTypeDisplayName}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Update the properties for this {data.type}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {Object.entries(groupedFields).map(([groupName, fields]) => {
            const isCollapsed = collapsedGroups[groupName]
            const groupDisplayName = GROUP_DISPLAY_NAMES[groupName] || groupName

            return (
              <Collapsible
                key={groupName}
                open={!isCollapsed}
                onOpenChange={() => toggleGroup(groupName)}
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-sm font-semibold bg-muted/50 rounded-md hover:bg-muted transition-colors">
                  <span>{groupDisplayName}</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${isCollapsed ? 'transform rotate-180' : ''}`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  {fields.map(field => renderField(field))}
                </CollapsibleContent>
              </Collapsible>
            )
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="sm:mr-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || Object.keys(validationErrors).length > 0}
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="w-4 h-4 mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Delivery Items Manager for VRP Capacity System - Shopping cart style interface
interface DeliveryItem {
  productId: string
  quantity: number
  unit: string // Required by backend validation
  product?: any // Product details from backend
}

interface DeliveryItemsManagerProps {
  value: DeliveryItem[]
  onChange: (items: DeliveryItem[]) => void
  projectId: string
  availableVehicles?: any[]
  selectedVehicleId?: string
  products?: any[]
  className?: string
  disabled?: boolean
  showCapacityWarnings?: boolean
  isLoading?: boolean
  error?: string | null
  onRetryLoadProducts?: () => void
  onCreateProduct?: () => void
}

const DeliveryItemsManager: React.FC<DeliveryItemsManagerProps> = ({
  value = [],
  onChange,
  projectId,
  availableVehicles = [],
  selectedVehicleId,
  products = [],
  className,
  disabled = false,
  showCapacityWarnings = true,
  isLoading = false,
  error = null,
  onRetryLoadProducts,
  onCreateProduct,
}) => {
  const [isAddingItem, setIsAddingItem] = useState(false)
  const [productSearch, setProductSearch] = useState('')
  const [selectedCompartmentFilter, setSelectedCompartmentFilter] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<any>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [isValidatingCapacity, setIsValidatingCapacity] = useState(false)

  // Migration effect: ensure existing items have unit field
  useEffect(() => {
    const needsMigration = value.some(item => !item.unit)
    if (needsMigration && products?.length > 0) {
      const migratedItems = value.map(item => {
        if (!item.unit) {
          const product = products.find(p => p._id === item.productId)
          return { ...item, unit: product?.unitType || 'units' }
        }
        return item
      })
      onChange(migratedItems)
    }
  }, [value, products, onChange])

  // Debounced search effect for better UX
  useEffect(() => {
    if (productSearch.trim()) {
      setIsSearching(true)
      const timer = setTimeout(() => {
        setIsSearching(false)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setIsSearching(false)
    }
  }, [productSearch])

  // Get available compartment types from products
  const availableCompartmentTypes = useMemo(() => {
    if (!products || !Array.isArray(products)) return []

    const types = new Set<string>()
    products.forEach(product => {
      if (product?.compartmentType) {
        types.add(product.compartmentType)
      }
    })
    return Array.from(types).sort()
  }, [products])

  // Filter products based on search and compartment filter
  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return []

    let filtered = products

    // Apply search filter
    if (productSearch.trim()) {
      const searchLower = productSearch.toLowerCase()
      filtered = filtered.filter(
        product =>
          product.name?.toLowerCase().includes(searchLower) ||
          product.category?.toLowerCase().includes(searchLower) ||
          product.unitType?.toLowerCase().includes(searchLower) ||
          product.compartmentType?.toLowerCase().includes(searchLower)
      )
    }

    // Apply compartment filter
    if (selectedCompartmentFilter) {
      filtered = filtered.filter(
        product => product.compartmentType === selectedCompartmentFilter
      )
    }

    return filtered
  }, [products, productSearch, selectedCompartmentFilter])

  // Calculate total capacity requirements
  const totalCapacityUsed = value.reduce(
    (acc, item) => {
      if (!item || !item.productId || !products || !Array.isArray(products)) {
        return acc
      }

      const product = products.find(p => p?._id === item.productId)
      if (!product) return acc

      const weight = (product.weightPerUnitKg || 0) * (item.quantity || 0)
      const volume = (product.volumePerUnitM3 || 0) * (item.quantity || 0)
      const count = item.quantity || 0

      return [acc[0] + weight, acc[1] + volume, acc[2] + count]
    },
    [0, 0, 0]
  )

  // Get selected vehicle capacity
  const selectedVehicle = availableVehicles?.find(
    v => v?._id === selectedVehicleId
  )
  const vehicleCapacity = selectedVehicle?.capacity || [0, 0, 0]

  // Handle adding new item with capacity validation loading
  const handleAddItem = async (item: {
    productId: string
    quantity: number
    unit: string
  }) => {
    console.log('🔥 handleAddItem called with:', item)
    setIsValidatingCapacity(true)

    try {
      // Validate required fields
      if (!item.productId || !item.unit || item.quantity <= 0) {
        console.error('❌ Validation failed:', {
          productId: item.productId,
          unit: item.unit,
          quantity: item.quantity,
        })
        toast.error('Invalid item data. Please check product and quantity.')
        return
      }

      console.log('✅ Validation passed, current value:', value)
      const existingIndex = value.findIndex(i => i.productId === item.productId)

      if (existingIndex >= 0) {
        // Update existing item
        console.log('📝 Updating existing item at index:', existingIndex)
        const updatedItems = [...value]
        updatedItems[existingIndex].quantity += item.quantity
        console.log('🚀 Calling onChange with updated items:', updatedItems)
        onChange(updatedItems)
      } else {
        // Add new item with unit field
        console.log('➕ Adding new item:', item)
        const newItems = [...value, { ...item, unit: item.unit }]
        console.log('🚀 Calling onChange with new items:', newItems)
        onChange(newItems)
      }

      // Small delay to show validation in progress
      await new Promise(resolve => setTimeout(resolve, 200))

      setIsAddingItem(false)
    } catch (error) {
      console.error('Error adding delivery item:', error)
      toast.error('Failed to add item. Please try again.')
    } finally {
      setIsValidatingCapacity(false)
    }
  }

  // Handle removing item
  const handleRemoveItem = (productId: string) => {
    onChange(value.filter(item => item.productId !== productId))
  }

  // Handle quantity change
  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveItem(productId)
    } else {
      onChange(
        value.map(item =>
          item.productId === productId
            ? { ...item, quantity: newQuantity }
            : item
        )
      )
    }
  }

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Enhanced Header with Loading States */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Package className="w-4 h-4" />
              Delivery Items
            </Label>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <LoadingSpinner size="sm" />
                <span>Loading products...</span>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" />
                <span>Failed to load</span>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsAddingItem(true)}
            disabled={disabled || isLoading || !!error}
          >
            {isValidatingCapacity ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Validating...
              </>
            ) : (
              '+ Add Product'
            )}
          </Button>
        </div>

        {/* Error State Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>Failed to Load Products</AlertTitle>
            <AlertDescription className="mt-2 space-y-3">
              <div>{error}</div>
              <div className="flex items-center gap-2">
                {onRetryLoadProducts && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRetryLoadProducts}
                    disabled={isLoading}
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                    />
                    Retry
                  </Button>
                )}
                {onCreateProduct && (
                  <Button variant="outline" size="sm" onClick={onCreateProduct}>
                    <Package className="w-4 h-4 mr-2" />
                    Create Product
                  </Button>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Enhanced Add Item Form with Advanced Search */}
        {isAddingItem && (
          <div className="border rounded-md p-4 bg-muted/20 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Add Product</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsAddingItem(false)
                  setProductSearch('')
                  setSelectedCompartmentFilter('')
                  setSelectedProduct(null)
                }}
              >
                Cancel
              </Button>
            </div>

            {/* Advanced Product Search */}
            <div className="space-y-3">
              {/* Enhanced Search Input with Loading States */}
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search products by name, category, or type..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="pr-10"
                  disabled={isLoading || !!error}
                  aria-label="Search products"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                  {isSearching ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Search className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </div>

              {/* Compartment Filter */}
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">
                  Filter by compartment:
                </Label>
                <Select
                  value={selectedCompartmentFilter}
                  onValueChange={setSelectedCompartmentFilter}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="All products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All products</SelectItem>
                    {availableCompartmentTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() +
                          type.slice(1).replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Enhanced Filtered Products List with Loading States */}
              <div className="max-h-64 overflow-y-auto border rounded-md">
                {/* Loading State */}
                {isLoading ? (
                  <div
                    className="space-y-2 p-2"
                    role="status"
                    aria-live="polite"
                    aria-label="Loading products"
                  >
                    <SkeletonProductCard />
                    <SkeletonProductCard />
                    <SkeletonProductCard />
                    <div className="text-center py-2">
                      <span className="text-sm text-muted-foreground">
                        Loading products...
                      </span>
                    </div>
                  </div>
                ) : /* Error State */ error ? (
                  <div className="p-6 text-center space-y-4">
                    <div className="flex items-center justify-center gap-2 text-destructive">
                      <AlertCircle className="w-6 h-6" />
                      <span className="font-semibold text-base">
                        Unable to load products
                      </span>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        There was a problem connecting to the database
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Please check your connection and try again
                      </p>
                    </div>
                    {onRetryLoadProducts && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onRetryLoadProducts}
                        disabled={isLoading}
                        className="font-semibold"
                      >
                        <RefreshCw
                          className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                        />
                        {isLoading ? 'Retrying...' : 'Try Again'}
                      </Button>
                    )}
                  </div>
                ) : /* Empty States */ !filteredProducts ||
                  filteredProducts.length === 0 ? (
                  <div className="p-4 text-center space-y-3">
                    {productSearch || selectedCompartmentFilter ? (
                      /* No Search Results */
                      <div
                        className="space-y-3"
                        role="status"
                        aria-live="polite"
                      >
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Search className="w-6 h-6" />
                          <span className="font-semibold text-base">
                            No products match your search
                          </span>
                        </div>
                        <div className="text-center space-y-2">
                          <p className="text-sm text-muted-foreground">
                            Try different keywords or clear your filters
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Search by name, category, or compartment type
                          </p>
                        </div>
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setProductSearch('')
                              setSelectedCompartmentFilter('')
                            }}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Clear All Filters
                          </Button>
                          {onCreateProduct && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={onCreateProduct}
                            >
                              <Package className="w-4 h-4 mr-2" />
                              Create New Product
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* No Products Available */
                      <div
                        className="space-y-3"
                        role="status"
                        aria-live="polite"
                      >
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Package className="w-6 h-6" />
                          <span className="font-semibold text-base">
                            No products available
                          </span>
                        </div>
                        <div className="text-center space-y-2">
                          <p className="text-sm text-muted-foreground">
                            Create products first to enable delivery item
                            selection
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Products define what items can be delivered (fuel,
                            packages, materials, etc.)
                          </p>
                        </div>
                        {onCreateProduct && (
                          <div className="flex justify-center">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={onCreateProduct}
                              className="font-semibold"
                            >
                              <Package className="w-4 h-4 mr-2" />
                              Create Your First Product
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Products List */ <div className="space-y-1 p-1">
                    {filteredProducts.map(product => (
                      <div
                        key={product._id}
                        className={cn(
                          'p-3 rounded-md cursor-pointer transition-colors border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                          selectedProduct?._id === product._id
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background hover:bg-muted/50 border-transparent'
                        )}
                        onClick={() =>
                          setSelectedProduct(
                            selectedProduct?._id === product._id
                              ? null
                              : product
                          )
                        }
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedProduct(
                              selectedProduct?._id === product._id
                                ? null
                                : product
                            )
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={selectedProduct?._id === product._id}
                        aria-label={`Select ${product.name} for delivery`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">
                              {product.name}
                            </div>
                            <div className="text-sm text-muted-foreground mt-2">
                              {product.category && (
                                <Badge
                                  variant="outline"
                                  className="mr-2 text-sm"
                                >
                                  {product.category}
                                </Badge>
                              )}
                              <span>{product.unitType}</span>
                              {product.compartmentType && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-sm">
                                  {product.compartmentType.replace(/_/g, ' ')}
                                </span>
                              )}
                            </div>

                            {/* Product Details */}
                            {selectedProduct?._id === product._id && (
                              <div className="mt-3 p-2 bg-muted/30 rounded-md text-sm space-y-1">
                                <div className="grid grid-cols-2 gap-2">
                                  {product.weightPerUnitKg && (
                                    <div>
                                      Weight: {product.weightPerUnitKg} kg/unit
                                    </div>
                                  )}
                                  {product.volumePerUnitM3 && (
                                    <div>
                                      Volume: {product.volumePerUnitM3} m³/unit
                                    </div>
                                  )}
                                  {product.densityKgPerUnit && (
                                    <div>
                                      Density: {product.densityKgPerUnit}{' '}
                                      kg/unit
                                    </div>
                                  )}
                                  {product.stackable !== undefined && (
                                    <div>
                                      Stackable:{' '}
                                      {product.stackable ? 'Yes' : 'No'}
                                    </div>
                                  )}
                                  {product.requiresRefrigeration && (
                                    <div className="text-blue-600">
                                      Requires refrigeration
                                    </div>
                                  )}
                                  {product.hazmatClass && (
                                    <div className="text-orange-600">
                                      HAZMAT: {product.hazmatClass}
                                    </div>
                                  )}
                                </div>
                                {product.handlingInstructions && (
                                  <div className="mt-2 text-muted-foreground">
                                    Instructions: {product.handlingInstructions}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Selected Product */}
              {selectedProduct && (
                <div className="flex items-center justify-between p-3 bg-primary/5 rounded-md border border-primary/20">
                  <div>
                    <div className="font-semibold">{selectedProduct.name}</div>
                    <div className="text-sm text-muted-foreground">
                      Ready to add • {selectedProduct.unitType}
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      handleAddItem({
                        productId: selectedProduct._id,
                        quantity: 1,
                        unit: selectedProduct.unitType || 'units',
                      })
                      setSelectedProduct(null)
                      setProductSearch('')
                      setSelectedCompartmentFilter('')
                    }}
                  >
                    Add Product
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Enhanced Items List with Loading and Empty States */}
        {isLoading && value.length === 0 ? (
          <div className="space-y-2">
            <SkeletonProductCard />
            <SkeletonProductCard />
          </div>
        ) : value.length === 0 ? (
          <div
            className="text-center py-8 space-y-4 border-2 border-dashed rounded-md bg-muted/5"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Package className="w-6 h-6" />
              <span className="text-lg font-semibold">
                No delivery items selected
              </span>
            </div>
            <div className="text-center space-y-2 max-w-sm mx-auto">
              <p className="text-sm text-muted-foreground">
                {error
                  ? 'Unable to load products. Please fix the error above to add delivery items.'
                  : isLoading
                    ? 'Loading available products...'
                    : !products || products.length === 0
                      ? 'Create products first to define what can be delivered at this location.'
                      : 'Add products and quantities to be delivered at this location.'}
              </p>
              {!error && !isLoading && products && products.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Each item will be validated against vehicle capacity limits
                </p>
              )}
            </div>
            {!error && !isLoading && products && products.length > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setIsAddingItem(true)}
                disabled={disabled}
                className="font-semibold"
              >
                <Package className="w-4 h-4 mr-2" />
                Add Your First Item
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {value.map(item => {
              const product = products.find(p => p._id === item.productId)
              if (!product) return null

              return (
                <div
                  key={item.productId}
                  className="flex items-center gap-4 p-3 border rounded-md"
                >
                  <div className="flex-1">
                    <div className="font-semibold">{product.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {product.category} • {product.unitType}
                      {product.weightPerUnitKg &&
                        ` • ${product.weightPerUnitKg}kg each`}
                      {product.volumePerUnitM3 &&
                        ` • ${product.volumePerUnitM3}m³ each`}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity - 1)
                      }
                      disabled={disabled}
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={e =>
                        handleQuantityChange(
                          item.productId,
                          parseInt(e.target.value) || 1
                        )
                      }
                      className="w-16 text-center"
                      min="1"
                      disabled={disabled}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity + 1)
                      }
                      disabled={disabled}
                    >
                      +
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(item.productId)}
                      disabled={disabled}
                      className="text-destructive hover:text-destructive"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Capacity Summary */}
        {value.length > 0 && showCapacityWarnings && (
          <div className="border rounded-md p-3 bg-muted/10">
            <Label className="text-sm font-normal mb-2 block">
              Capacity Requirements
            </Label>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="font-normal">Weight</div>
                <div>{totalCapacityUsed[0].toFixed(2)} kg</div>
              </div>
              <div>
                <div className="font-normal">Volume</div>
                <div>{totalCapacityUsed[1].toFixed(3)} m³</div>
              </div>
              <div>
                <div className="font-normal">Items</div>
                <div>{totalCapacityUsed[2]} items</div>
              </div>
            </div>

            {selectedVehicleId && selectedVehicle && (
              <div className="mt-3 pt-3 border-t">
                <Label className="text-sm font-normal">
                  Vehicle Capacity Check
                </Label>
                <div className="mt-2">
                  <div className="text-sm text-muted-foreground">
                    Vehicle: {selectedVehicle.description || 'Selected Vehicle'}
                  </div>
                  <div className="mt-2 space-y-1">
                    {totalCapacityUsed.map((used, index) => {
                      const capacity = vehicleCapacity[index] || 0
                      const percentage =
                        capacity > 0 ? (used / capacity) * 100 : 0
                      const label =
                        index === 0
                          ? 'Weight'
                          : index === 1
                            ? 'Volume'
                            : 'Count'
                      const isOverCapacity = percentage > 100

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{label}</span>
                          <span
                            className={
                              isOverCapacity
                                ? 'text-red-600 font-semibold'
                                : 'text-muted-foreground'
                            }
                          >
                            {percentage.toFixed(0)}%
                            {isOverCapacity && ' ⚠️ Over capacity'}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// PickupItemsManager - Mirror of DeliveryItemsManager for pickup operations
interface PickupItem {
  productId: string
  quantity: number
  product?: any // Product details from backend
}

interface PickupItemsManagerProps {
  value: PickupItem[]
  onChange: (items: PickupItem[]) => void
  projectId: string
  availableVehicles?: any[]
  selectedVehicleId?: string
  products?: any[]
  className?: string
  disabled?: boolean
  showCapacityWarnings?: boolean
  isLoading?: boolean
  error?: string | null
  onRetryLoadProducts?: () => void
  onCreateProduct?: () => void
}

const PickupItemsManager: React.FC<PickupItemsManagerProps> = ({
  value = [],
  onChange,
  projectId,
  availableVehicles = [],
  selectedVehicleId,
  products = [],
  className,
  disabled = false,
  showCapacityWarnings = true,
  isLoading = false,
  error = null,
  onRetryLoadProducts,
  onCreateProduct,
}) => {
  const [isAddingItem, setIsAddingItem] = useState(false)

  // Calculate total capacity requirements
  const totalCapacityUsed = value.reduce(
    (acc, item) => {
      if (!item || !item.productId || !products || !Array.isArray(products)) {
        return acc
      }

      const product = products.find(p => p?._id === item.productId)
      if (!product) return acc

      const weight = (product.weightPerUnitKg || 0) * (item.quantity || 0)
      const volume = (product.volumePerUnitM3 || 0) * (item.quantity || 0)
      const count = item.quantity || 0

      return [acc[0] + weight, acc[1] + volume, acc[2] + count]
    },
    [0, 0, 0]
  )

  // Get selected vehicle capacity
  const selectedVehicle = availableVehicles?.find(
    v => v?._id === selectedVehicleId
  )
  const vehicleCapacity = selectedVehicle?.capacity || [0, 0, 0]

  // Duplicate handleAddItem function removed - using the async version above

  // Handle removing item
  const handleRemoveItem = (productId: string) => {
    onChange(value.filter(item => item.productId !== productId))
  }

  // Handle quantity change
  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveItem(productId)
    } else {
      onChange(
        value.map(item =>
          item.productId === productId
            ? { ...item, quantity: newQuantity }
            : item
        )
      )
    }
  }

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Enhanced Header with Loading States */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Package className="w-4 h-4" />
              Pickup Items
            </Label>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <LoadingSpinner size="sm" />
                <span>Loading products...</span>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" />
                <span>Failed to load</span>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsAddingItem(true)}
            disabled={disabled || isLoading || !!error}
          >
            + Add Product
          </Button>
        </div>

        {/* Error State Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>Failed to Load Products</AlertTitle>
            <AlertDescription className="mt-2 space-y-3">
              <div>{error}</div>
              <div className="flex items-center gap-2">
                {onRetryLoadProducts && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRetryLoadProducts}
                    disabled={isLoading}
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                    />
                    Retry
                  </Button>
                )}
                {onCreateProduct && (
                  <Button variant="outline" size="sm" onClick={onCreateProduct}>
                    <Package className="w-4 h-4 mr-2" />
                    Create Product
                  </Button>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Add Item Modal/Form */}
        {isAddingItem && (
          <div className="border rounded-md p-4 bg-muted/20">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold">Add Product</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsAddingItem(false)}
              >
                Cancel
              </Button>
            </div>

            <Select
              onValueChange={async productId => {
                const selectedProduct = products?.find(p => p._id === productId)
                await handleAddItem({
                  productId,
                  quantity: 1,
                  unit: selectedProduct?.unitType || 'units',
                })
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a product..." />
              </SelectTrigger>
              <SelectContent>
                {products === undefined ? (
                  <SelectItem value="" disabled>
                    Loading products...
                  </SelectItem>
                ) : products &&
                  Array.isArray(products) &&
                  products.length > 0 ? (
                  products.map(product => (
                    <SelectItem key={product._id} value={product._id}>
                      <div className="flex justify-between w-full">
                        <span>{product.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">
                          {product.category} • {product.unitType}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                ) : products === null ? (
                  <SelectItem value="" disabled>
                    Error loading products - Please try again
                  </SelectItem>
                ) : (
                  <SelectItem value="" disabled>
                    No products available - Create your first product to get
                    started
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Items List */}
        {value.length === 0 ? (
          <div
            className="text-center py-8 space-y-4 border-2 border-dashed rounded-md bg-muted/5"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Package className="w-6 h-6" />
              <span className="text-lg font-semibold">
                No pickup items selected
              </span>
            </div>
            <div className="text-center space-y-2 max-w-sm mx-auto">
              <p className="text-sm text-muted-foreground">
                {error
                  ? 'Unable to load products. Please fix the error above to add pickup items.'
                  : isLoading
                    ? 'Loading available products...'
                    : !products || products.length === 0
                      ? 'Create products first to define what can be picked up from this location.'
                      : 'Add products and quantities to be picked up from this location.'}
              </p>
              {!error && !isLoading && products && products.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Each item will be validated against vehicle capacity limits
                </p>
              )}
            </div>
            {!error && !isLoading && products && products.length > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setIsAddingItem(true)}
                disabled={disabled}
                className="font-semibold"
              >
                <Package className="w-4 h-4 mr-2" />
                Add Your First Item
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {value.map(item => {
              const product = products.find(p => p._id === item.productId)
              if (!product) return null

              return (
                <div
                  key={item.productId}
                  className="flex items-center gap-4 p-3 border rounded-md"
                >
                  <div className="flex-1">
                    <div className="font-semibold">{product.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {product.category} • {product.unitType}
                      {product.weightPerUnitKg &&
                        ` • ${product.weightPerUnitKg}kg each`}
                      {product.volumePerUnitM3 &&
                        ` • ${product.volumePerUnitM3}m³ each`}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity - 1)
                      }
                      disabled={disabled}
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={e =>
                        handleQuantityChange(
                          item.productId,
                          parseInt(e.target.value) || 1
                        )
                      }
                      className="w-16 text-center"
                      min="1"
                      disabled={disabled}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity + 1)
                      }
                      disabled={disabled}
                    >
                      +
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(item.productId)}
                      disabled={disabled}
                      className="text-destructive hover:text-destructive"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Capacity Summary */}
        {value.length > 0 && showCapacityWarnings && (
          <div className="border rounded-md p-3 bg-muted/10">
            <Label className="text-sm font-normal mb-2 block">
              Capacity Requirements
            </Label>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="font-normal">Weight</div>
                <div>{totalCapacityUsed[0].toFixed(2)} kg</div>
              </div>
              <div>
                <div className="font-normal">Volume</div>
                <div>{totalCapacityUsed[1].toFixed(3)} m³</div>
              </div>
              <div>
                <div className="font-normal">Items</div>
                <div>{totalCapacityUsed[2]} items</div>
              </div>
            </div>

            {selectedVehicleId && selectedVehicle && (
              <div className="mt-3 pt-3 border-t">
                <Label className="text-sm font-normal">
                  Vehicle Capacity Check
                </Label>
                <div className="mt-2">
                  <div className="text-sm text-muted-foreground">
                    Vehicle: {selectedVehicle.description || 'Selected Vehicle'}
                  </div>
                  <div className="mt-2 space-y-1">
                    {totalCapacityUsed.map((used, index) => {
                      const capacity = vehicleCapacity[index] || 0
                      const percentage =
                        capacity > 0 ? (used / capacity) * 100 : 0
                      const label =
                        index === 0
                          ? 'Weight'
                          : index === 1
                            ? 'Volume'
                            : 'Count'
                      const isOverCapacity = percentage > 100

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{label}</span>
                          <span
                            className={
                              isOverCapacity
                                ? 'text-red-600 font-semibold'
                                : 'text-muted-foreground'
                            }
                          >
                            {percentage.toFixed(0)}%
                            {isOverCapacity && ' ⚠️ Over capacity'}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DeliveryItemsManager
export { PickupItemsManager }
export type {
  DeliveryItem,
  DeliveryItemsManagerProps,
  PickupItem,
  PickupItemsManagerProps,
}
